// ===========================================
// Gamification API
// ===========================================
// API client for gamification features - progress, achievements, pieces, skills

import { apiClient } from '../services/api';
import {
  StudentProgress,
  AdvanceLevelResult,
  ProgressStats,
  LeaderboardEntry,
  LeaderboardParams,
  GamificationLevelInfo,
  Achievement,
  AchievementDefinition,
  CreateAchievementDefinitionData,
  UpdateAchievementDefinitionData,
  AwardAchievementData,
  AwardSpecialAchievementData,
  AchievementDefinitionFilters,
  StudentAchievementFilters,
  RecentAchievementsFilters,
  Piece,
  CreatePieceData,
  UpdatePieceData,
  PieceFilters,
  PieceSkill,
  CreateSkillData,
  UpdateSkillData,
  ReorderSkillsData,
  StudentPiece,
  AssignPieceData,
  StudentPieceFilters,
  StudentPieceSkill,
  UpdateSkillProgressData,
  PaginatedResponse,
  LessonPiece,
  StudentLessonPiecesResult,
  ClassPieceProgressResult,
} from '../types/gamification.types';

const BASE_URL = '/gamification';

// ===========================================
// API RESPONSE WRAPPER
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// ===========================================
// GAMIFICATION LEVELS
// ===========================================

/**
 * Get school's gamification-enabled program levels
 */
export async function getGamificationLevels(): Promise<GamificationLevelInfo[]> {
  const response = await apiClient.get<ApiResponse<GamificationLevelInfo[]>>(
    `${BASE_URL}/levels`
  );
  return response.data;
}

// ===========================================
// STUDENT PROGRESS
// ===========================================

/**
 * Get current user's gamification progress
 */
export async function getMyProgress(): Promise<ProgressStats> {
  const response = await apiClient.get<ApiResponse<ProgressStats>>(`${BASE_URL}/progress`);
  return response.data;
}

/**
 * Get a student's progress (teacher/admin)
 */
export async function getStudentProgress(studentId: string): Promise<ProgressStats> {
  const response = await apiClient.get<ApiResponse<ProgressStats>>(
    `${BASE_URL}/progress/${studentId}`
  );
  return response.data;
}

/**
 * Get all students' progress (admin - paginated)
 * Backend: GET /progress?level=&limit=&offset=
 * Returns: { status, data: StudentProgress[], meta: { total } }
 */
export async function getAllProgress(params?: {
  page?: number;
  limit?: number;
  level?: string;
}): Promise<PaginatedResponse<StudentProgress>> {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 25;
  const offset = (page - 1) * limit;

  const response: { data: StudentProgress[]; meta?: { total: number } } =
    await apiClient.get(`${BASE_URL}/progress`, {
      params: {
        limit,
        offset,
        level: params?.level,
      },
    });

  const data = response.data ?? [];
  const total = response.meta?.total ?? data.length;
  return {
    data,
    pagination: {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit),
    },
  };
}

/**
 * Advance a student's level (teacher/admin)
 */
export async function advanceStudentLevel(studentId: string): Promise<AdvanceLevelResult> {
  const response = await apiClient.post<ApiResponse<AdvanceLevelResult>>(
    `${BASE_URL}/progress/${studentId}/advance-level`
  );
  return response.data;
}

/**
 * Get school leaderboard
 */
export async function getLeaderboard(params?: LeaderboardParams): Promise<LeaderboardEntry[]> {
  const response = await apiClient.get<ApiResponse<LeaderboardEntry[]>>(
    `${BASE_URL}/leaderboard`,
    { params }
  );
  return response.data;
}

// ===========================================
// ACHIEVEMENTS
// ===========================================

/**
 * Get current user's achievements
 */
export async function getMyAchievements(
  params?: StudentAchievementFilters
): Promise<PaginatedResponse<Achievement>> {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<Achievement>>>(
    `${BASE_URL}/achievements`,
    { params }
  );
  return response.data;
}

/**
 * Get a student's achievements (teacher/admin)
 */
export async function getStudentAchievements(
  studentId: string,
  params?: StudentAchievementFilters
): Promise<Achievement[]> {
  const response = await apiClient.get<ApiResponse<Achievement[]>>(
    `${BASE_URL}/students/${studentId}/achievements`,
    { params }
  );
  return response.data;
}

/**
 * Get recent achievements school-wide
 */
export async function getRecentAchievements(
  params?: RecentAchievementsFilters
): Promise<Achievement[]> {
  const response = await apiClient.get<ApiResponse<Achievement[]>>(
    `${BASE_URL}/achievements/recent`,
    { params }
  );
  return response.data;
}

/**
 * Award an achievement to a student (teacher/admin)
 */
export async function awardAchievement(
  studentId: string,
  data: AwardAchievementData
): Promise<Achievement> {
  const response = await apiClient.post<ApiResponse<Achievement>>(
    `${BASE_URL}/achievements/award`,
    { studentId, ...data }
  );
  return response.data;
}

/**
 * Award a special (custom) achievement to a student (teacher/admin)
 */
export async function awardSpecialAchievement(
  studentId: string,
  data: AwardSpecialAchievementData
): Promise<Achievement> {
  const response = await apiClient.post<ApiResponse<Achievement>>(
    `${BASE_URL}/achievements/award-special`,
    { studentId, ...data }
  );
  return response.data;
}

/**
 * Revoke an achievement (admin only)
 */
export async function revokeAchievement(achievementId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/achievements/${achievementId}`);
}

// ===========================================
// ACHIEVEMENT DEFINITIONS (Admin)
// ===========================================

/**
 * Get all achievement definitions
 */
export async function getAchievementDefinitions(
  params?: AchievementDefinitionFilters
): Promise<AchievementDefinition[]> {
  const response = await apiClient.get<ApiResponse<AchievementDefinition[]>>(
    `${BASE_URL}/achievement-definitions`,
    { params }
  );
  return response.data;
}

/**
 * Get a single achievement definition
 */
export async function getAchievementDefinition(id: string): Promise<AchievementDefinition> {
  const response = await apiClient.get<ApiResponse<AchievementDefinition>>(
    `${BASE_URL}/achievement-definitions/${id}`
  );
  return response.data;
}

/**
 * Create an achievement definition (admin)
 */
export async function createAchievementDefinition(
  data: CreateAchievementDefinitionData
): Promise<AchievementDefinition> {
  const response = await apiClient.post<ApiResponse<AchievementDefinition>>(
    `${BASE_URL}/achievement-definitions`,
    data
  );
  return response.data;
}

/**
 * Update an achievement definition (admin)
 */
export async function updateAchievementDefinition(
  id: string,
  data: UpdateAchievementDefinitionData
): Promise<AchievementDefinition> {
  const response = await apiClient.patch<ApiResponse<AchievementDefinition>>(
    `${BASE_URL}/achievement-definitions/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete an achievement definition (admin)
 */
export async function deleteAchievementDefinition(id: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/achievement-definitions/${id}`);
}

// ===========================================
// PIECE LIBRARY
// ===========================================

/**
 * Get all pieces with optional filters
 */
export async function getPieces(params?: PieceFilters): Promise<PaginatedResponse<Piece>> {
  // The two callers (PieceLibraryPage grid, AssignPieceToLessonModal) render the
  // full result with no pager, so default to the backend's max page size (100)
  // rather than a small page that would silently truncate a school's library.
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 100;
  const offset = (page - 1) * limit;

  const response: { data: Piece[]; meta?: { total: number } } = await apiClient.get(
    `${BASE_URL}/pieces`,
    {
      params: {
        instrumentId: params?.instrumentId,
        difficulty: params?.difficulty,
        programLevelId: params?.programLevelId,
        isActive: params?.isActive,
        search: params?.search,
        limit,
        offset,
      },
    }
  );

  const data = response.data ?? [];
  const total = response.meta?.total ?? data.length;
  return {
    data,
    pagination: {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit),
    },
  };
}

/**
 * Get a single piece with skills
 */
export async function getPiece(pieceId: string): Promise<Piece> {
  const response = await apiClient.get<ApiResponse<Piece>>(`${BASE_URL}/pieces/${pieceId}`);
  return response.data;
}

/**
 * Create a new piece (teacher/admin)
 */
export async function createPiece(data: CreatePieceData): Promise<Piece> {
  const response = await apiClient.post<ApiResponse<Piece>>(`${BASE_URL}/pieces`, data);
  return response.data;
}

/**
 * Update a piece (teacher/admin)
 */
export async function updatePiece(pieceId: string, data: UpdatePieceData): Promise<Piece> {
  const response = await apiClient.patch<ApiResponse<Piece>>(
    `${BASE_URL}/pieces/${pieceId}`,
    data
  );
  return response.data;
}

/**
 * Delete a piece (admin)
 */
export async function deletePiece(pieceId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/pieces/${pieceId}`);
}

// ===========================================
// PIECE SKILLS
// ===========================================

/**
 * Add a skill to a piece (teacher/admin)
 */
export async function addSkillToPiece(
  pieceId: string,
  data: CreateSkillData
): Promise<PieceSkill> {
  const response = await apiClient.post<ApiResponse<PieceSkill>>(
    `${BASE_URL}/pieces/${pieceId}/skills`,
    data
  );
  return response.data;
}

/**
 * Update a skill (teacher/admin)
 */
export async function updateSkill(skillId: string, data: UpdateSkillData): Promise<PieceSkill> {
  const response = await apiClient.patch<ApiResponse<PieceSkill>>(
    `${BASE_URL}/skills/${skillId}`,
    data
  );
  return response.data;
}

/**
 * Delete a skill (admin)
 */
export async function deleteSkill(skillId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/skills/${skillId}`);
}

/**
 * Reorder skills within a piece (teacher/admin)
 */
export async function reorderSkills(pieceId: string, data: ReorderSkillsData): Promise<void> {
  await apiClient.put(`${BASE_URL}/pieces/${pieceId}/skills/reorder`, data);
}

// ===========================================
// LESSON PLANS
// ===========================================

/**
 * Upload a lesson-plan PDF to Google Drive and attach it to a piece, or to a stage
 * when skillId is given. Returns the updated Piece (piece-level) or PieceSkill (stage).
 */
export async function uploadLessonPlan(
  pieceId: string,
  file: File,
  skillId?: string
): Promise<Piece | PieceSkill> {
  const formData = new FormData();
  formData.append('file', file);
  if (skillId) {
    formData.append('skillId', skillId);
  }

  const response = await apiClient.post<ApiResponse<Piece | PieceSkill>>(
    `${BASE_URL}/pieces/${pieceId}/lesson-plan`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

/**
 * Clear a lesson plan on a piece, or on a stage when skillId is given.
 */
export async function clearLessonPlan(
  pieceId: string,
  skillId?: string
): Promise<Piece | PieceSkill> {
  const query = skillId ? `?skillId=${encodeURIComponent(skillId)}` : '';
  const response = await apiClient.delete<ApiResponse<Piece | PieceSkill>>(
    `${BASE_URL}/pieces/${pieceId}/lesson-plan${query}`
  );
  return response.data;
}

// ===========================================
// STUDENT PIECES (ASSIGNMENTS)
// ===========================================

/**
 * Get current user's assigned pieces (student/parent)
 */
export async function getMyPieces(
  params?: StudentPieceFilters
): Promise<PaginatedResponse<StudentPiece>> {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<StudentPiece>>>(
    `${BASE_URL}/student-pieces`,
    { params }
  );
  return response.data;
}

/**
 * Get a student's assigned pieces (teacher/admin)
 */
export async function getStudentPieces(
  studentId: string,
  params?: StudentPieceFilters
): Promise<PaginatedResponse<StudentPiece>> {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<StudentPiece>>>(
    `${BASE_URL}/student-pieces/${studentId}`,
    { params }
  );
  return response.data;
}

/**
 * Get detailed student piece with skill progress
 */
export async function getStudentPieceDetail(studentPieceId: string): Promise<StudentPiece> {
  const response = await apiClient.get<ApiResponse<StudentPiece>>(
    `${BASE_URL}/student-pieces/detail/${studentPieceId}`
  );
  return response.data;
}

/**
 * Assign a piece to a student (teacher/admin)
 */
export async function assignPiece(
  studentId: string,
  data: AssignPieceData
): Promise<StudentPiece> {
  const response = await apiClient.post<ApiResponse<StudentPiece>>(
    `${BASE_URL}/student-pieces/${studentId}/assign`,
    data
  );
  return response.data;
}

/**
 * Unassign a piece from a student (teacher/admin)
 */
export async function unassignPiece(studentPieceId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/student-pieces/${studentPieceId}`);
}

// ===========================================
// SKILL PROGRESS
// ===========================================

/**
 * Update skill progress status (teacher/admin)
 */
export async function updateSkillProgress(
  skillProgressId: string,
  data: UpdateSkillProgressData
): Promise<StudentPieceSkill> {
  const response = await apiClient.patch<ApiResponse<StudentPieceSkill>>(
    `${BASE_URL}/skill-progress/${skillProgressId}`,
    data
  );
  return response.data;
}

/**
 * Mark a skill as in progress (teacher/admin)
 */
export async function startSkill(skillProgressId: string): Promise<StudentPieceSkill> {
  const response = await apiClient.post<ApiResponse<StudentPieceSkill>>(
    `${BASE_URL}/skill-progress/${skillProgressId}/start`
  );
  return response.data;
}

/**
 * Mark a skill as complete (teacher/admin)
 */
export async function completeSkill(
  skillProgressId: string,
  notes?: string
): Promise<StudentPieceSkill> {
  const response = await apiClient.post<ApiResponse<StudentPieceSkill>>(
    `${BASE_URL}/skill-progress/${skillProgressId}/complete`,
    { notes }
  );
  return response.data;
}

// ===========================================
// LESSON PIECES (CLASS ASSIGNMENTS)
// ===========================================

/**
 * Get all pieces assigned to a lesson/class
 */
export async function getLessonPieces(lessonId: string): Promise<LessonPiece[]> {
  const response = await apiClient.get<ApiResponse<LessonPiece[]>>(
    `${BASE_URL}/lessons/${lessonId}/pieces`
  );
  return response.data;
}

/**
 * Assign a piece to a lesson/class (admin only)
 */
export async function assignPieceToLesson(
  lessonId: string,
  pieceId: string
): Promise<LessonPiece> {
  const response = await apiClient.post<ApiResponse<LessonPiece>>(
    `${BASE_URL}/lessons/${lessonId}/pieces`,
    { pieceId }
  );
  return response.data;
}

/**
 * Remove a piece from a lesson/class (admin only)
 */
export async function removePieceFromLesson(
  lessonId: string,
  pieceId: string
): Promise<void> {
  await apiClient.delete(`${BASE_URL}/lessons/${lessonId}/pieces/${pieceId}`);
}

/**
 * Toggle isCurrent status for a lesson piece (teacher/admin)
 */
export async function toggleLessonPieceCurrent(
  lessonPieceId: string,
  isCurrent: boolean
): Promise<LessonPiece> {
  const response = await apiClient.patch<ApiResponse<LessonPiece>>(
    `${BASE_URL}/lesson-pieces/${lessonPieceId}`,
    { isCurrent }
  );
  return response.data;
}

/**
 * Get current and past pieces for a student across enrolled classes
 */
export async function getStudentLessonPieces(
  studentId: string
): Promise<StudentLessonPiecesResult> {
  const response = await apiClient.get<ApiResponse<StudentLessonPiecesResult>>(
    `${BASE_URL}/students/${studentId}/lesson-pieces`
  );
  return response.data;
}

/**
 * Get all enrolled students and their skill progress for a class piece (teacher/admin)
 */
export async function getClassPieceProgress(
  lessonId: string,
  pieceId: string
): Promise<ClassPieceProgressResult> {
  const response = await apiClient.get<ApiResponse<ClassPieceProgressResult>>(
    `${BASE_URL}/lessons/${lessonId}/pieces/${pieceId}/progress`
  );
  return response.data;
}
